import { CloseOutlined, Error } from '@mui/icons-material';
import { CssBaseline, IconButton } from '@mui/material';
import { styled, ThemeProvider } from '@mui/material/styles';
import { Font } from '@react-pdf/renderer';
import {
  MaterialDesignContent,
  SnackbarKey,
  SnackbarProvider,
  useSnackbar,
} from 'notistack';
import { useMemo } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import theme from './assets/theme';
import AppContext from './context/app-context';
import ErrorBoundary from './pages/error-boundary.page';
import Router from './shared/partials/router.partial';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import '@fontsource/roboto/400-italic.css';

import robotoLight from '@fontsource/roboto/files/roboto-latin-300-normal.woff';
import robotoItalic from '@fontsource/roboto/files/roboto-latin-400-italic.woff';
import robotoRegular from '@fontsource/roboto/files/roboto-latin-400-normal.woff';
import robotoBold from '@fontsource/roboto/files/roboto-latin-700-normal.woff';

import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';

import '@fontsource/montserrat/400-italic.css';

import montserratLight from '@fontsource/montserrat/files/montserrat-latin-300-normal.woff';
import montserratItalic from '@fontsource/montserrat/files/montserrat-latin-400-italic.woff';
import montserratRegular from '@fontsource/montserrat/files/montserrat-latin-400-normal.woff';
import montserratMedium from '@fontsource/montserrat/files/montserrat-latin-500-normal.woff';
import montserratBold from '@fontsource/montserrat/files/montserrat-latin-700-normal.woff';

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: robotoRegular,
      fontWeight: 400,
    }, // Regular
    {
      src: robotoLight,
      fontWeight: 300,
    }, // Light
    {
      src: robotoBold,
      fontWeight: 700,
    }, // Bold
    {
      src: robotoItalic,
      fontWeight: 400,
      fontStyle: 'italic',
    }, // Italic
  ],
});

Font.register({
  family: 'Montserrat',
  fonts: [
    {
      src: montserratRegular,
      fontWeight: 400,
    }, // Regular
    {
      src: montserratLight,
      fontWeight: 300,
    }, // Light
    {
      src: montserratMedium,
      fontWeight: 500,
    }, // Medium
    {
      src: montserratBold,
      fontWeight: 700,
    }, // Bold
    {
      src: montserratItalic,
      fontWeight: 400,
      fontStyle: 'italic',
    }, // Italic
  ],
});

export const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  () => ({
    '&.notistack-MuiContent-success': {
      backgroundColor: '#22C55E',
    },
    '&.notistack-MuiContent-error': {
      backgroundColor: '#FF5630',
    },
    '&.notistack-MuiContent-info': {
      backgroundColor: '#00B8D9',
    },
    '&.notistack-MuiContent-warning': {
      backgroundColor: '#FFAB00',
    },
    '#notistack-snackbar': {
      padding: '4px 0',
      maxWidth: '600px',
    },
    '&.notistack-MuiContent': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      flexWrap: 'nowrap',
    },
  }),
);

export const SnackbarCloseButton = ({
  snackbarKey,
}: {
  snackbarKey: SnackbarKey;
}) => {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton
      className='notistack-close'
      onClick={() => closeSnackbar(snackbarKey)}
      sx={{ bgcolor: 'transparent', ':hover': { bgcolor: 'transparent' } }}
    >
      <CloseOutlined style={{ fontSize: '16px', color: '#FFF' }} />
    </IconButton>
  );
};

const PageMeta = () => {
  const { pathname } = useLocation();
  const page = pathname?.split('/')?.[1] || '';

  const moduleTitle = useMemo(() => {
    switch (page) {
      case 'front-office':
        return 'Xenia Cloud Front Office';
      case 'point-of-sale':
        return 'iServe Cloud POS';
      case 'back-office':
        return 'iServe Cloud POS';
      case 'accounting-and-finance':
        return 'Hermes Cloud';
      case 'users-management':
        return 'Users Management';
      default:
        return 'Xenia Cloud PMS';
    }
  }, [page]);

  return (
    <Helmet>
      <title>{moduleTitle}</title>
    </Helmet>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme()}>
        <CssBaseline enableColorScheme />
        <SnackbarProvider
          maxSnack={3}
          action={(snackbarKey) => (
            <SnackbarCloseButton snackbarKey={snackbarKey} />
          )}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          Components={{
            success: StyledMaterialDesignContent,
            error: StyledMaterialDesignContent,
            warning: StyledMaterialDesignContent,
            info: StyledMaterialDesignContent,
            default: StyledMaterialDesignContent,
          }}
          iconVariant={{
            error: <Error sx={{ mr: 1 }} />,
          }}
          preventDuplicate
        >
          <ErrorBoundary>
            <AppContext>
              <PageMeta />
              <Router />
            </AppContext>
          </ErrorBoundary>
        </SnackbarProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
